import { Injectable, signal } from '@angular/core';
import { debugLog } from '../../helpers/debugLog';

export const VOICE_RECORDING_MIME_TYPE = 'audio/wav';

const TARGET_SAMPLE_RATE = 16000;
const CHANNEL_COUNT = 1;
const WORKLET_MODULE_PATH = 'audio/pcm-recorder.worklet.js';
const WORKLET_PROCESSOR_NAME = 'pcm-recorder';
const BYTES_PER_SAMPLE = 2;
const BITS_PER_SAMPLE = 16;
const HEADER_BYTES = 44;
const FMT_CHUNK_BYTES = 16;
const PCM_FORMAT = 1;
const RIFF_PREAMBLE_BYTES = 8;
const UINT32_BYTES = 4;
const UINT16_BYTES = 2;
const MAX_INT16 = 32767;

function createAudioContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
  } catch (error) {
    debugLog('AudioContext rejected the target sample rate, resampling instead:', error);
    return new AudioContext();
  }
}

function mergeBuffers(buffers: Float32Array[], totalLength: number): Float32Array {
  const merged = new Float32Array(totalLength);
  let offset = 0;

  for (const buffer of buffers) {
    merged.set(buffer, offset);
    offset += buffer.length;
  }

  return merged;
}

export function downsample(samples: Float32Array, sourceSampleRate: number): Float32Array {
  if (sourceSampleRate === TARGET_SAMPLE_RATE) return samples;

  const ratio = sourceSampleRate / TARGET_SAMPLE_RATE;
  const targetLength = Math.floor(samples.length / ratio);
  const downsampled = new Float32Array(targetLength);

  for (let index = 0; index < targetLength; index++) {
    const start = Math.floor(index * ratio);
    const end = Math.min(Math.floor((index + 1) * ratio), samples.length);
    let sum = 0;

    for (let sampleIndex = start; sampleIndex < end; sampleIndex++) {
      sum += samples[sampleIndex];
    }

    downsampled[index] = end > start ? sum / (end - start) : 0;
  }

  return downsampled;
}

export function encodeWav(samples: Float32Array): Blob {
  const dataBytes = samples.length * BYTES_PER_SAMPLE;
  const view = new DataView(new ArrayBuffer(HEADER_BYTES + dataBytes));
  let offset = 0;

  const writeText = (text: string): void => {
    for (const character of text) {
      view.setUint8(offset, character.charCodeAt(0));
      offset += 1;
    }
  };

  const writeUint32 = (value: number): void => {
    view.setUint32(offset, value, true);
    offset += UINT32_BYTES;
  };

  const writeUint16 = (value: number): void => {
    view.setUint16(offset, value, true);
    offset += UINT16_BYTES;
  };

  writeText('RIFF');
  writeUint32(HEADER_BYTES - RIFF_PREAMBLE_BYTES + dataBytes);
  writeText('WAVE');
  writeText('fmt ');
  writeUint32(FMT_CHUNK_BYTES);
  writeUint16(PCM_FORMAT);
  writeUint16(CHANNEL_COUNT);
  writeUint32(TARGET_SAMPLE_RATE);
  writeUint32(TARGET_SAMPLE_RATE * CHANNEL_COUNT * BYTES_PER_SAMPLE);
  writeUint16(CHANNEL_COUNT * BYTES_PER_SAMPLE);
  writeUint16(BITS_PER_SAMPLE);
  writeText('data');
  writeUint32(dataBytes);

  for (const sample of samples) {
    view.setInt16(offset, Math.max(-1, Math.min(1, sample)) * MAX_INT16, true);
    offset += BYTES_PER_SAMPLE;
  }

  return new Blob([view.buffer], { type: VOICE_RECORDING_MIME_TYPE });
}

@Injectable({
  providedIn: 'root',
})
export class VoiceRecorderService {
  private readonly recording = signal(false);

  private audioContext?: AudioContext;
  private mediaStream?: MediaStream;
  private sourceNode?: MediaStreamAudioSourceNode;
  private processorNode?: AudioWorkletNode;
  private silentNode?: GainNode;
  private recordedBuffers: Float32Array[] = [];
  private recordedLength = 0;

  readonly isRecording = this.recording.asReadonly();

  readonly mimeType = VOICE_RECORDING_MIME_TYPE;

  async start(): Promise<void> {
    if (this.recording()) return;

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: CHANNEL_COUNT },
    });

    const audioContext = createAudioContext();
    await audioContext.audioWorklet.addModule(new URL(WORKLET_MODULE_PATH, document.baseURI).href);

    const sourceNode = audioContext.createMediaStreamSource(this.mediaStream);
    const processorNode = new AudioWorkletNode(audioContext, WORKLET_PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: CHANNEL_COUNT,
    });
    const silentNode = audioContext.createGain();
    silentNode.gain.value = 0;

    this.recordedBuffers = [];
    this.recordedLength = 0;

    processorNode.port.onmessage = (event: MessageEvent<Float32Array>): void => {
      this.recordedBuffers.push(event.data);
      this.recordedLength += event.data.length;
    };

    sourceNode.connect(processorNode);
    processorNode.connect(silentNode);
    silentNode.connect(audioContext.destination);

    this.audioContext = audioContext;
    this.sourceNode = sourceNode;
    this.processorNode = processorNode;
    this.silentNode = silentNode;
    this.recording.set(true);
  }

  async stop(): Promise<Blob | undefined> {
    if (!this.recording()) return undefined;

    const sourceSampleRate = this.audioContext?.sampleRate ?? TARGET_SAMPLE_RATE;
    const samples = mergeBuffers(this.recordedBuffers, this.recordedLength);

    await this.release();

    return encodeWav(downsample(samples, sourceSampleRate));
  }

  async cancel(): Promise<void> {
    await this.release();
  }

  private async release(): Promise<void> {
    if (this.processorNode) {
      this.processorNode.port.onmessage = null;
      this.processorNode.port.close();
      this.processorNode.disconnect();
    }
    this.silentNode?.disconnect();
    this.sourceNode?.disconnect();
    this.mediaStream?.getTracks().forEach((track) => {
      track.stop();
    });
    if (this.audioContext && this.audioContext.state !== 'closed') {
      await this.audioContext.close();
    }

    this.processorNode = undefined;
    this.silentNode = undefined;
    this.sourceNode = undefined;
    this.mediaStream = undefined;
    this.audioContext = undefined;
    this.recordedBuffers = [];
    this.recordedLength = 0;
    this.recording.set(false);
  }
}
